import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputHidden from 'components/Form/InputHidden';
import InputPassword from 'components/Form/InputPassword';
import SubmitButton from 'components/Form/SubmitButton';
import SideBySide from 'components/Layout/SideBySide';
import LinkButton from 'components/LinkButton';
import {t} from 'ttag';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';
import {
    ProfileChangePasswordFormFields,
    usePostProfileChangePassword,
} from './services';

export type ProfileChangePasswordProps = {
    onSuccess?: (formData: ProfileChangePasswordFormData) => void;
};

type ProfileChangePasswordFormData =
    FormFields<ProfileChangePasswordFormFields>;

const ChangePassword: FC = () => {
    const methods = useForm<ProfileChangePasswordFormData>();
    const {getValues, handleSubmit} = methods;

    const {mutateAsync} = usePostProfileChangePassword();

    const onSubmit = async (formData: ProfileChangePasswordFormData) => {
        await mutateAsync(formData)
            .then(() => {
                setUrl(window.Urls.profile_view());
            })
            .catch(onApiFormError(methods));
    };

    return (
        <FormProvider {...methods}>
            <form className="hide-required" onSubmit={handleSubmit(onSubmit)}>
                <SideBySide className="max-w-xl">
                    <FormHeader hideHR={true} title={t`Change Your Password`} />
                    <InputHidden name="action" value="Change Password" />
                    <InputPassword
                        hasValidation={false}
                        label={t`Current Password`}
                        name="currentPassword"
                        required={true}
                    />
                    <InputPassword
                        label={t`New Password`}
                        name="newPassword"
                        required={true}
                        showPasswordRules={true}
                    />
                    <InputPassword
                        label={t`New Password Confirmation`}
                        name="newPasswordConfirmation"
                        rules={{
                            required: true,
                            validate: (value) =>
                                value === getValues('newPassword') ||
                                t`Passwords do not match`,
                        }}
                    />
                    <FormResult />
                    <FormActions align="left">
                        <SubmitButton>{t`Change Password`}</SubmitButton>
                        <LinkButton
                            href={window.Urls.profile_view()}
                            kind="tertiary"
                        >
                            {t`Cancel`}
                        </LinkButton>
                    </FormActions>
                </SideBySide>
            </form>
        </FormProvider>
    );
};

export default ChangePassword;
